import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IoTimeOutline,
  IoVideocamOutline,
  IoChatbubbleOutline,
  IoCloseOutline,
  IoCalendarOutline,
} from "react-icons/io5";

import { getRecentDoctorBooking } from "../api/bookings.api";
import { getAssetUrl } from "../api/config";
import { getStaffId } from "../utils/storage";
import LoadingState from "./States/LoadingState";

// Popup duration: 10 seconds
const POPUP_DURATION = 10 * 1000;

// Show Join Meet starting 30 minutes before the consultation
const JOIN_BEFORE_MINUTES = 30;

// Check meeting eligibility every 30 seconds
const CHECK_INTERVAL = 30 * 1000;

/**
 * Get the scheduled start time.
 * Prefer the backend timestamp when available.
 */
const getScheduledStart = (booking) => {
  if (!booking) return null;

  const scheduledStart =
    booking.zegoRoom?.scheduledStart ||
    booking.meeting_details?.scheduledStart;

  if (scheduledStart) {
    const parsed = new Date(scheduledStart);

    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const dateValue = booking.bookedSlot?.date || booking.date;
  const timeValue = booking.bookedSlot?.timeSlot || booking.timeSlot;

  if (dateValue && timeValue) {
    const datePart = String(dateValue).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    const timePart = String(timeValue).match(
      /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
    );

    if (datePart && timePart) {
      const [, year, month, day] = datePart;
      const [, hours, minutes, seconds = "0"] = timePart;

      const parsed = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds),
        0
      );

      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }

  return null;
};

const getScheduledEnd = (booking, start) => {
  const scheduledEnd =
    booking?.zegoRoom?.scheduledEnd ||
    booking?.meeting_details?.scheduledEnd;

  if (scheduledEnd) {
    const parsed = new Date(scheduledEnd);

    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // Default consultation duration: 30 minutes.
  return start
    ? new Date(start.getTime() + 30 * 60 * 1000)
    : null;
};

const isTomorrow = (dateStr) => {
  if (!dateStr) return false;

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) return false;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear()
  );
};

const formatBookingDate = (booking) => {
  const start = getScheduledStart(booking);

  if (!start) return "";

  return start.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const UpcomingWidget = () => {
  const navigate = useNavigate();
  const staffId = getStaffId();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  // Fetch the latest booking.
  const fetchBooking = useCallback(async () => {
    if (!staffId) {
      setBooking(null);
      setLoading(false);
      return;
    }

    try {
      const response = await getRecentDoctorBooking(staffId);
      setBooking(response?.data?.booking || null);
    } catch (error) {
      console.error("Failed to fetch recent doctor booking:", error);
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  // Keep the time current so Join Meet appears automatically.
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(Date.now());
    }, CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  // Resolve the scheduled appointment time.
  const scheduledStart = getScheduledStart(booking);
  const scheduledEnd = getScheduledEnd(booking, scheduledStart);

  const scheduledStartTime = scheduledStart?.getTime();
  const scheduledEndTime = scheduledEnd?.getTime();

  // Join becomes available 30 minutes before the start time.
  const joinAvailableAt =
    scheduledStartTime != null
      ? scheduledStartTime - JOIN_BEFORE_MINUTES * 60 * 1000
      : null;

  // A consultation is completed when its scheduled end time has passed.
  const isMeetingCompleted =
    scheduledEndTime != null && currentTime >= scheduledEndTime;

  // Show the popup for 10 seconds only if the consultation has not ended.
  useEffect(() => {
    if (
      !booking ||
      scheduledStartTime == null ||
      scheduledEndTime == null ||
      Date.now() >= scheduledEndTime
    ) {
      setShowPopup(false);
      return;
    }

    setShowPopup(true);

    const timeoutId = setTimeout(() => {
      setShowPopup(false);
    }, POPUP_DURATION);

    return () => clearTimeout(timeoutId);
  }, [
    booking?._id,
    booking?.bookingId,
    scheduledStartTime,
    scheduledEndTime,
  ]);

  // Hide the popup automatically when the consultation ends.
  useEffect(() => {
    if (isMeetingCompleted) {
      setShowPopup(false);
    }
  }, [isMeetingCompleted]);

  const isJoinTime =
    booking?.type === "Online" &&
    scheduledStartTime != null &&
    scheduledEndTime != null &&
    joinAvailableAt != null &&
    currentTime >= joinAvailableAt &&
    currentTime < scheduledEndTime;

  // API returns _id, not necessarily bookingId.
  const bookingId = booking?.bookingId || booking?._id;
  const doctor = booking?.doctorId || booking?.doctor;

  const handleJoinMeet = () => {
    if (!bookingId || !isJoinTime) return;

    navigate(`/consultations/video/${bookingId}`, {
      state: {
        doctor: doctor || null,
        booking,
      },
    });
  };

  if (loading) {
    return (
      <div className="card p-4">
        <LoadingState
          branded
          brandedCompact
          message="Loading…"
          className="py-4"
        />
      </div>
    );
  }

  return (
    <>
      {/* Main upcoming consultation widget - UI unchanged */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-on-primary-fixed-var">
            Upcoming
          </h3>

          {booking &&
            isTomorrow(booking.date || booking.bookedSlot?.date) && (
              <span className="text-xs font-medium bg-primary-fixed text-primary px-2.5 py-1 rounded-full">
                Tomorrow
              </span>
            )}
        </div>

        {!booking ? (
          <p className="text-sm text-on-surface-variant">
            No upcoming consultations.
          </p>
        ) : (
          <>
            <div className="flex items-start gap-3 mb-4">
              {doctor?.image ? (
                <img
                  src={getAssetUrl(doctor.image)}
                  alt={doctor.name || "Doctor"}
                  className="w-12 h-12 rounded-full object-cover border border-surface-container"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src =
                      "https://placehold.co/48x48?text=Dr";
                  }}
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary-fixed flex items-center justify-center text-primary font-bold text-sm">
                  Dr
                </div>
              )}

              <div className="min-w-0">
                <p className="font-semibold text-on-primary-fixed-var">
                  {doctor?.name || "Doctor"}
                </p>

                <p className="text-xs text-on-surface-variant mt-0.5">
                  {doctor?.specialization || "General Physician"}
                  {booking.type === "Online"
                    ? " • Video Consult"
                    : booking.type === "Offline"
                      ? " • Clinic Visit"
                      : ""}
                </p>

                <p className="text-xs text-on-surface-variant mt-1 flex items-center gap-1">
                  <IoTimeOutline size={12} />

                  {formatBookingDate(booking)}

                  {scheduledStart
                    ? ` • ${scheduledStart.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}`
                    : ""}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {doctor?._id && (
                <Link
                  to={`/chat?doctor=${doctor._id}`}
                  className="w-full flex items-center justify-center gap-2 text-sm py-2.5 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary-fixed/20"
                >
                  <IoChatbubbleOutline size={18} />
                  Chat
                </Link>
              )}

              {booking.type === "Online" && isJoinTime && bookingId && (
                <button
                  type="button"
                  onClick={handleJoinMeet}
                  className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                >
                  <IoVideocamOutline size={18} />
                  Join Meet
                </button>
              )}

              {booking.type === "Online" &&
                !isJoinTime &&
                joinAvailableAt != null &&
                currentTime < joinAvailableAt && (
                  <p className="text-xs text-on-surface-variant text-center">
                    Join Meet will be available 30 minutes before your
                    consultation.
                  </p>
                )}
            </div>
          </>
        )}
      </div>

      {/* Original popup UI - only visibility logic changed */}
      {showPopup &&
        booking &&
        scheduledEndTime != null &&
        currentTime < scheduledEndTime && (
          <div className="fixed bottom-5 right-5 z-[9999] w-[calc(100%-2.5rem)] max-w-sm animate-in slide-in-from-right-4 fade-in duration-300">
            <div className="bg-white text-gray-900 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-start gap-3 p-4">
                <div className="w-11 h-11 shrink-0 rounded-full bg-primary text-white flex items-center justify-center">
                  <IoCalendarOutline size={23} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">
                    Upcoming Consultation
                  </p>

                  <p className="text-sm text-gray-600 mt-1">
                    Your consultation with{" "}
                    <span className="font-medium text-gray-900">
                      {doctor?.name || "Doctor"}
                    </span>
                    .
                  </p>

                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <IoTimeOutline size={13} />

                    {formatBookingDate(booking)}

                    {scheduledStart
                      ? ` • ${scheduledStart.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}`
                      : ""}
                  </p>

                  {booking.type === "Online" &&
                    isJoinTime &&
                    bookingId && (
                      <button
                        type="button"
                        onClick={handleJoinMeet}
                        className="mt-3 w-full min-h-10 rounded-lg bg-primary hover:bg-primary text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                      >
                        <IoVideocamOutline size={18} />
                        Join Meet
                      </button>
                    )}

                  {booking.type === "Online" &&
                    !isJoinTime &&
                    scheduledStart &&
                    joinAvailableAt != null &&
                    currentTime < joinAvailableAt && (
                      <p className="text-xs text-gray-500 mt-3">
                        The Join Meet button will appear 30 minutes before
                        your scheduled time.
                      </p>
                    )}
                </div>

                <button
                  type="button"
                  onClick={() => setShowPopup(false)}
                  aria-label="Dismiss notification"
                  className="shrink-0 p-1 rounded-full hover:bg-gray-100 text-gray-500"
                >
                  <IoCloseOutline size={20} />
                </button>
              </div>

              <div className="h-1 bg-gray-100">
                <div
                  className="h-full bg-primary"
                  style={{
                    animation: "upcomingPopupProgress 10s linear forwards",
                  }}
                />
              </div>
            </div>

            <style>{`
              @keyframes upcomingPopupProgress {
                from { width: 100%; }
                to { width: 0%; }
              }
            `}</style>
          </div>
        )}
    </>
  );
};

export default UpcomingWidget;